import React from "react";
import { Alert, AlertTitle } from "@mui/material";

import { Progress } from "ui";

import { apiSlice, versionsSlice } from "fond/api";
import { useCustomWorkflow } from "fond/hooks/useCustomWorkflow";
import useWorkflowErrorLogs from "fond/hooks/useWorkflowErrorLogs";
import { setVersion } from "fond/project/redux";
import { WorkflowKey } from "fond/types";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";
import { BlockSpinner } from "fond/widgets";

const stageConfig: Record<string, { label: string }> = {
  partition: { label: "Partitioning data" },
  prepare: { label: "Preparing inputs" },
  bom: { label: "Counting materials and costs" },
  c2s: { label: "Calculating cost to serve" },
};

interface DataModificationPanelProps {
  workflowKey: WorkflowKey;
}

const DataModificationPanel: React.FC<DataModificationPanelProps> = ({ workflowKey }) => {
  const dispatch = useAppDispatch();
  const versionId = useAppSelector((state) => state.project.versionId);
  const projectId = useAppSelector((state) => state.project.projectId);
  const { workflowState, stages, isLoading, logs } = useCustomWorkflow({
    versionId,
    key: workflowKey,
    onComplete: async () => {
      const { getVersions } = versionsSlice.endpoints;

      // Invalidate the Versions tag to ensure we fetch the latest versions
      await dispatch(apiSlice.util.invalidateTags([{ type: "Versions", id: projectId }]));

      let result = dispatch(getVersions.initiate(projectId, { forceRefetch: true }));
      await result;
      let timeout = 10000;
      // Poll until loading finishes
      while (result.isLoading && timeout > 0) {
        await new Promise((resolve) => {
          setTimeout(resolve, 1000);
        });
        timeout -= 1000;
        result.unsubscribe();
        result = dispatch(getVersions.initiate(projectId, { forceRefetch: true }));
        await result;
      }
      const versions = result.data;
      result.unsubscribe();

      // Navigate to the latest version
      const latestVersion = versions?.ids && versions.ids.length > 1 ? versions.ids[0] : null;
      if (latestVersion) {
        dispatch(setVersion(latestVersion));
      }
    },
  });
  const { errorLogsInline, logsModal } = useWorkflowErrorLogs(logs);
  const isGeneratingCTS = workflowState === "STARTING" || workflowState === "IN_PROGRESS";

  if (isLoading) return <BlockSpinner />;

  return (
    <>
      {workflowState === "ERROR" && (
        <Alert severity="error" variant="compact" sx={{ mb: 1 }}>
          <AlertTitle>Cost to serve generation failed</AlertTitle>
          Cost to serve could not be generated.
          {errorLogsInline}
        </Alert>
      )}

      {stages && isGeneratingCTS && (
        <Alert sx={{ mb: 1 }} severity="info">
          <AlertTitle>Generating new scenario</AlertTitle>
        </Alert>
      )}

      {stages && (isGeneratingCTS || workflowState === "ERROR") && (
        <Progress steps={stages.map(({ key, status }) => ({ ID: key, Label: stageConfig[key].label, Status: status }))} />
      )}

      {logsModal}
    </>
  );
};

export default DataModificationPanel;
